/**
 * Noticing that the page is out of date.
 *
 * A tab can stay open for hours and iOS never reloads it, so a deployed fix
 * simply does not reach the person looking at the screen. One user spent an
 * hour and three attempts hitting a bug that had already been fixed, with no
 * way to know — and no way for us to tell, short of reading the server logs.
 *
 * The entry bundle's filename carries its content hash, so comparing the one
 * this page is running against the one the server is serving answers it with
 * no build plumbing and no version file to keep in step.
 */

const BUNDLE = /assets\/index-[A-Za-z0-9_-]+\.js/;

/** The bundle this page actually loaded, read from its own script tag. */
function running(): string | null {
  for (const s of document.querySelectorAll('script[type="module"][src]')) {
    const m = s.getAttribute('src')?.match(BUNDLE);
    if (m) return m[0];
  }
  return null;
}

async function deployed(): Promise<string | null> {
  // `cache: no-store` matters: the whole point is to bypass the copy that is
  // making this page stale in the first place.
  const res = await fetch('/', { cache: 'no-store' });
  if (!res.ok) return null;
  return (await res.text()).match(BUNDLE)?.[0] ?? null;
}

export async function updateAvailable(): Promise<boolean> {
  const here = running();
  if (!here) return false;
  try {
    const there = await deployed();
    return there !== null && there !== here;
  } catch {
    return false; // Offline is not out of date.
  }
}

/**
 * Checks on returning to the tab and every half hour. Returning is the case
 * that matters: it is exactly when someone picks the phone back up and tries
 * the thing that failed before.
 */
export function watchForUpdate(onFound: () => void): () => void {
  let stopped = false;
  const check = async () => {
    if (stopped || document.hidden) return;
    if (await updateAvailable()) { stopped = true; onFound(); }
  };
  const onVisible = () => { if (!document.hidden) void check(); };
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => void check(), 30 * 60 * 1000);
  void check();
  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}
